<template>
    <div class="flex flex-col tracking-wider w-full max-w-[530px] p-8 border border-gray-100 lg:border-gray-300 rounded-md bg-gray-100 text-gray-700">
        <div class="text-xl pb-6 border-b border-gray-300 w-full flex justify-center text-center">Reset Your Password</div>
        <div v-if="isLoading" class="flex flex-col justify-between items-center pt-8">
            <div class="w-full flex justify-center items-center">
                Please wait while we reset your password.
            </div>
            <div class="w-12 h-12 py-8">
                <LoadingSpinner />
            </div>
        </div>
        <div v-else class="flex flex-col w-full">
            <div v-if="resetPasswordResponseType" class="pt-6 flex flex-col space-y-4" >
                <div v-if="resetPasswordResponseType=='success'" class="flex flex-col justify-center items-center space-y-4">
                    <div class="w-full whitespace-pre-wrap pretty text-center">Success! Your password has been reset.</div>
                    <div class="w-full whitespace-pre-wrap pretty text-center"><tracked-link :link="{ path: `/auth`, label: 'Sign in', displayLabel: false, class: 'inline'}"><span class="text-indigo-600 underline underline-offset-2 font-semibold hover:text-indigo-900 cursor-pointer">Sign In</span></tracked-link></div>
                </div>
                <div v-else-if="resetPasswordResponseType=='error'">
                    <div>
                        <div v-if="resetPasswordResponse=='invalidToken'"  class="w-full whitespace-pre-wrap pretty text-center">
                            Your token is invalid. Please click <tracked-link :link="{ path: `/change-password/null`, label: 'Forgot Password', displayLabel: false, class: 'inline'}"><span class="text-indigo-600 underline underline-offset-2 font-semibold hover:text-indigo-900 cursor-pointer">here</span></tracked-link> to send another reset link.
                        </div>
                        <div v-else>
                            <div class="pb-6 w-full whitespace-pre-wrap pretty text-center">Error: {{ resetPasswordResponse }}</div>
                            <div>
                                <button 
                                    @click="clearResetPasswordResponse"
                                    class="px-4 py-3 bg-dark-primary text-white font-semibold  leading-tight rounded-md transition duration-150 ease-in-out w-full"
                                    :class="{ 'hover:bg-[#181B4E] hover:shadow-md focus:shadow-lg focus:outline-none focus:ring-0 active:shadow-lg' : validForm, 'opacity-50' : !validForm}"
                                >
                                    Retry
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <form v-else @submit.prevent="submitResetPassword" action="" class="h-full w-full flex flex-col justify-center items-center space-y-4 pt-6">
                <div class="py-3 w-full flex flex-col space-y-4">
                    <div class="w-full">
                        <input class="p-3 w-full border border-gray-400 rounded-md" :class="{'border-red-400 bg-red-50 text-red-700' : password1InvalidMessage}" required type="password" id="password1" autocomplete="new-password" placeholder="Password" v-model="passwordResetData.new_password1">
                    </div>
                    <!-- <InvalidPasswordMessage v-if="password1InvalidMessage" :invalid-password-message="password1InvalidMessage" /> -->
                    <!-- <div v-if="password1InvalidMessage" class="text-xs text-red-700 font-normal">
                        <span class="text-base">&#x26A0;</span> {{ password1InvalidMessage }}
                    </div> -->
                    <div class="w-full">
                        <input class="p-3 w-full border border-gray-400 rounded-md"  :class="{'border-red-400 bg-red-50 text-red-700' : password2InvalidMessage}" required type="password" id="password2" autocomplete="new-password" placeholder="Confirm Password" v-model="passwordResetData.new_password2">
                    </div>
                    <PasswordRequirements />
                    <div class="h-3">
                        <InvalidPasswordMessage v-if="password1InvalidMessage" :invalid-password-message="password1InvalidMessage" />
                        <NonMatchingPasswordsMessage v-else-if="nonMatchingPasswords" />
                    </div>
                    <!-- <InvalidPasswordMessage v-if="password2InvalidMessage" :invalid-password2message="password1InvalidMessage" /> -->
                    

                    <!-- <div v-if="password2InvalidMessage" class="text-xs text-red-700 font-normal">
                        <span class="text-base">&#x26A0;</span> {{ password2InvalidMessage }}
                    </div>
                    <div v-else-if="nonMatchingPasswords" class="text-xs text-red-700 font-normal">
                        <span class="text-base">&#x26A0;</span> Passwords do not match
                    </div> -->
                </div>
                <div class="w-full pt-6 border-t border-gray-300">
                    <button 
                        type="submit"
                        :disabled="!validForm"
                        class="px-4 py-3 bg-dark-primary text-white font-semibold  leading-tight rounded-md transition duration-150 ease-in-out w-full"
                        :class="{ 'hover:bg-[#181B4E] hover:shadow-md focus:shadow-lg focus:outline-none focus:ring-0 active:shadow-lg' : validForm, 'opacity-50' : !validForm}"
                    >
                        Submit
                    </button>
                </div>   
            </form>
        </div>
    </div>
</template>

<script setup>
import { ref, reactive, computed, defineProps } from 'vue';
import PasswordRequirements from './PasswordRequirements.vue';
import InvalidPasswordMessage from './InvalidPasswordMessage.vue';
import NonMatchingPasswordsMessage from './NonMatchingPasswordsMessage.vue';
import { resetPassword } from '@/services/Auth';
import TrackedLink from '../nav-bar-link-components/TrackedLink.vue';
import LoadingSpinner from '../base-components/LoadingSpinner.vue';
import { handleRestAuthResponseError } from '@/composables/djRestAuth';

import { useRouter } from 'vue-router';

const props = defineProps(['uid', 'token'])

const router = useRouter()

const isLoading = ref(false)
const resetPasswordResponse = ref(null)
const resetPasswordResponseType = ref(null)

const passwordResetData = reactive({
    uid: props.uid && props.uid != 'null' ? props.uid : null,
    token: props.token && props.token != 'null' ? props.token : null,
    new_password1: null,
    new_password2: null,
})

const password1InvalidMessage = computed(() => {
    if(passwordResetData.new_password1 && passwordResetData.new_password1.length < 8) {
        return 'Password must be at least 8 characters'
    }
    return null
})

const password2InvalidMessage = computed(() => {
    if(passwordResetData.new_password2 && passwordResetData.new_password2.length < 8) {
        return 'Password must be at least 8 characters'
    }
    return null
})
const nonMatchingPasswords = computed(() => {
    if(passwordResetData.new_password1 && passwordResetData.new_password2) {
        if(passwordResetData.new_password1 != passwordResetData.new_password2) {
            return true
        }
    }
    return false
})

const validForm = computed(() => {
    let isValid = false
    if(passwordResetData.uid && passwordResetData.token && passwordResetData.new_password1 && passwordResetData.new_password2) {
        isValid = true
    }
    if(nonMatchingPasswords.value || password1InvalidMessage.value || password2InvalidMessage.value) {
        isValid = false
    }
    return isValid
})

function clearResetPasswordResponse() {
    resetPasswordResponse.value = null
    resetPasswordResponseType.value = null
    passwordResetData.new_password1 = null
    passwordResetData.new_password2 = null
}

async function submitResetPassword() {
    console.log('submitResetPassword')
    isLoading.value = true
    const payload = JSON.stringify(passwordResetData)
    console.log(payload)
    const response = await resetPassword(payload)
    console.log('response')
    console.log(response)

    setTimeout(() => {
        isLoading.value = false
        if(response && response.status == 200) {
            resetPasswordResponseType.value = 'success'
            // setTimeout(() => {
            //     router.push('/login')
            // }, 4000)
        } else {
            resetPasswordResponseType.value = 'error'
            let responseData = response.response.data
            if(responseData.token && responseData.token.length > 0 && responseData.token[0] == 'Invalid value') {
                resetPasswordResponse.value = 'invalidToken'
            } else if(responseData.new_password2) {
                resetPasswordResponse.value = responseData.new_password2
            } else {
                resetPasswordResponse.value = handleRestAuthResponseError(response)
            }
        }
    }, 2000)
}
</script>